import i18n from '../../i18n/i18n';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { STATUS } from '../../constants';
import { RootState } from '../store/index';
import {
  IGetTeacherByIdResponseBody,
  IGetTeacherDetailsRequestParams,
  IGetTeachersResponseBody,
  ICreateTeacherRequestBody,
  ICreateTeacherResponseBody,
  ITeacherProfile,
  IGetAssetId,
  ISendInvitationRequest,
  IGetTeacherRosterClassroomsResponse,
} from '../../interface/teacherModel';
import {
  getTeacherDataByID,
  putEditTeacher,
  deleteTeacherDataByID,
  createTeacher,
  getAssetId,
  getTeacherRosterClassrooms,
} from '../../services/teacher';
import api from '../../services/api';
import { ICreateStudentError, ISendInvitationResponse } from '../../interface/studentModel';
import { ICreateSubjectError } from '../../interface/subjectModel';
import { IErrorResponse } from '../../interface';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';

// State interface
interface ITeacherState {
  isLoading: boolean;
  error?: string;
  message?: string;
  status?: string;
  teacherData?: ITeacherProfile[];
}

// Default state
const DEFAULT_STATE: ITeacherState = {
  isLoading: false,
  error: undefined,
  message: undefined,
  status: undefined,
  teacherData: undefined,
};

// Thunk for deleting teacher data by ID
export const deleteTeacherData = createAsyncThunk<
  IGetTeacherByIdResponseBody,
  string, // ID as parameter
  { rejectValue: string }
>('teacher/deleteTeacherData', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteTeacherDataByID(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const errorResponse =
        err.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error');
      return rejectWithValue(errorResponse);
    } else {
      return rejectWithValue(err.message);
    }
  }
});

// Thunk for fetching teacher data
export const fetchTeacherData = createAsyncThunk<
  IGetTeachersResponseBody,
  IGetTeacherDetailsRequestParams | void, // <-- pass params directly or undefined
  { rejectValue: string }
>('teacher/fetchTeacherData', async (params, { rejectWithValue }) => {
  try {
    // If params is undefined, queryParams becomes an empty object.
    // If params exists, it spreads them without forcing a default for isActive.
    const queryParams: IGetTeacherDetailsRequestParams = {
      ...(params ?? {}),
    };

    const response = await api.get<IGetTeachersResponseBody>('/teacher', { params: queryParams });
    return response.data;
  } catch (err: any) {
    let errorMessage = i18n.t('toast.failed_to_fetch_teacher_data', 'Failed to fetch teacher data');

    if (axios.isAxiosError(err)) {
      errorMessage = err.response?.data?.message || err.message || errorMessage;
    } else if (err instanceof Error) {
      errorMessage = err.message || errorMessage;
    }

    return rejectWithValue(errorMessage);
  }
});

/**
 * CVA-4153/4155 — classrooms the CALLING teacher is rostered to, used to scope
 * the Add Exam wizard's Curriculum/Grade/Classroom pickers for a Teacher
 * (non EXAM_MANAGEMENT) caller. Deliberately NOT wired into `ITeacherState` /
 * `extraReducers` below: this is wizard-scoped, ephemeral data consumed once
 * via `.unwrap()` into local component state (see `ExamEventMainForm.tsx`,
 * which already does the same for `getgrade` via `gradeDropDown`) rather than
 * a new global slice for two small dropdown-source arrays.
 */
export const fetchTeacherRosterClassrooms = createAsyncThunk<
  IGetTeacherRosterClassroomsResponse,
  void,
  { rejectValue: IErrorResponse }
>('teacher/fetchTeacherRosterClassrooms', async (_, { rejectWithValue }) => {
  try {
    const response = await getTeacherRosterClassrooms();
    return response;
  } catch (err) {
    return rejectWithValue(
      normalizeAxiosError(
        err,
        i18n.t('toast.failed_to_fetch_teacher_data', 'Failed to fetch teacher data'),
      ),
    );
  }
});

const handleDownload = (response: AxiosResponse<ArrayBuffer>): void => {
  // Create an object URL from the response data (which is an ArrayBuffer)
  const fileUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = 'report.pdf'; // You can customize the filename here
  link.click();
  URL.revokeObjectURL(fileUrl);
};

export const fetchTeacherReport = createAsyncThunk<
  IGetTeachersResponseBody,
  void,
  { rejectValue: ICreateSubjectError }
>('teacher/getTeacherReport', async (_, { rejectWithValue }) => {
  try {
    await api
      .get('/teacher/download', { responseType: 'arraybuffer' })
      .then((response: AxiosResponse<ArrayBuffer>) => handleDownload(response));
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const teacherSendInvitation = createAsyncThunk<
  ISendInvitationResponse,
  ISendInvitationRequest,
  { rejectValue: ICreateStudentError }
>('student/sendInvitation', async (body, { rejectWithValue }) => {
  try {
    const response = await api.post('/teacher/resend', body);
    return response.data;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(
        errorResponse || {
          message: i18n.t('msg.unknown_error', i18n.t('toast.unknown_error', 'Unknown error')),
        },
      );
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});
// Thunk for fetching teacher data by ID
export const fetchTeacherDataByID = createAsyncThunk<
  IGetTeacherByIdResponseBody,
  string, // ID as parameter
  { rejectValue: string }
>('teacher/fetchTeacherDataByID', async (id, { rejectWithValue }) => {
  try {
    const response = await getTeacherDataByID(id);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const errorResponse =
        err.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error');
      return rejectWithValue(errorResponse);
    } else {
      return rejectWithValue(err.message);
    }
  }
});

// Thunk for editing teacher data by ID
export const editTeacherData = createAsyncThunk<
  IGetTeacherByIdResponseBody,
  { id: string; data: ICreateTeacherRequestBody }, // ID and request body as parameters
  { rejectValue: IErrorResponse }
>('teacher/editTeacherData', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await putEditTeacher(id, data);
    return response;
  } catch (err) {
    // Shared normalizer (repo convention). api.tsx rejects with the response
    // BODY, not an AxiosError, so the old axios.isAxiosError branch never
    // matched and the backend's own message was at risk of being replaced by a
    // generic fallback. This keeps that message intact for the page's toast.
    return rejectWithValue(
      normalizeAxiosError(err, i18n.t('toast.unknown_error', 'Unknown error')),
    );
  }
});

export const getAsset = createAsyncThunk<IGetAssetId, FormData, { rejectValue: string }>(
  'teacher/getId',
  async (data, { rejectWithValue }) => {
    try {
      const response = await getAssetId(data);
      return response;
    } catch (err: any) {
      let errorMessage = i18n.t('toast.unknown_error', 'Unknown error');

      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || i18n.t('toast.request_failed', 'Request failed');
      } else {
        errorMessage = err.message;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

// Thunk for adding a new teacher
export const addTeacher = createAsyncThunk<
  ICreateTeacherResponseBody,
  ICreateTeacherRequestBody,
  { rejectValue: IErrorResponse }
>('teacher/addTeacher', async (data, { rejectWithValue }) => {
  try {
    const response = await createTeacher(data);
    return response;
  } catch (err) {
    // See editTeacherData — keeps the backend's message intact for the toast.
    return rejectWithValue(
      normalizeAxiosError(err, i18n.t('toast.unknown_error', 'Unknown error')),
    );
  }
});

// Slice for teacher state management
const teacherSlice = createSlice({
  name: 'teacher',
  initialState: DEFAULT_STATE,
  reducers: {
    resetTeacherState: () => DEFAULT_STATE,
  },
  extraReducers: (builder) => {
    // Fetch teacher data
    builder.addCase(fetchTeacherData.pending, (state) => {
      state.isLoading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      fetchTeacherData.fulfilled,
      (state, action: PayloadAction<IGetTeachersResponseBody>) => {
        state.isLoading = false;
        state.teacherData = action.payload?.data?.teacherProfile;
        //state.message = action.payload;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      fetchTeacherData.rejected,
      (state, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    // Fetch teacher data by ID
    builder.addCase(fetchTeacherDataByID.pending, (state) => {
      state.isLoading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      fetchTeacherDataByID.fulfilled,
      (state, action: PayloadAction<IGetTeacherByIdResponseBody>) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      fetchTeacherDataByID.rejected,
      (state, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    // Edit teacher data
    builder.addCase(editTeacherData.pending, (state) => {
      state.isLoading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      editTeacherData.fulfilled,
      (state, action: PayloadAction<IGetTeacherByIdResponseBody>) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      editTeacherData.rejected,
      (state, action: PayloadAction<IErrorResponse | undefined>) => {
        state.isLoading = false;
        state.error = action.payload?.message;
        state.status = STATUS.FAILED;
      },
    );

    // Delete teacher data
    builder.addCase(deleteTeacherData.pending, (state) => {
      state.isLoading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      deleteTeacherData.fulfilled,
      (state, action: PayloadAction<IGetTeacherByIdResponseBody>) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      deleteTeacherData.rejected,
      (state, action: PayloadAction<string | undefined>) => {
        state.isLoading = false;
        state.error = action.payload;
        state.status = STATUS.FAILED;
      },
    );

    // Add teacher data
    builder.addCase(addTeacher.pending, (state) => {
      state.isLoading = true;
      state.status = STATUS.LOADING;
    });
    builder.addCase(
      addTeacher.fulfilled,
      (state, action: PayloadAction<ICreateTeacherResponseBody>) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.status = STATUS.SUCCEEDED;
      },
    );
    builder.addCase(
      addTeacher.rejected,
      (state, action: PayloadAction<IErrorResponse | undefined>) => {
        state.isLoading = false;
        state.error = action.payload?.message;
        state.status = STATUS.FAILED;
      },
    );
  },
});

// Export actions and reducer
export const { resetTeacherState } = teacherSlice.actions;
export const teacherReducer = teacherSlice.reducer;

// Selectors
export const selectTeacherState = (state: RootState): ITeacherState => state.teacher;
export const selectTeacherStatus = (state: RootState): string | undefined => state.teacher.status;
export const selectTeacherError = (state: RootState): string | undefined => state.teacher.error;
export const selectTeacherMessage = (state: RootState): string | undefined => state.teacher.message;
export const selectTeacherData = (state: RootState): ITeacherProfile[] | undefined =>
  state.teacher.teacherData;

export default teacherReducer;
